import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSafeSelector } from '@unified-checkout/kit-components';
import { Button, ToggleBox } from '@unified-checkout/design-system';
import InstantRefundTermsModal from './modal';
import InfoIcon from '../../assets/icons/Vector.png';

interface IInstantRefundOptProps {
  onClick: (checked: boolean) => void;
  isLoading: boolean;
  forceDisabled?: boolean;
  forceDisabledMessage?: string;
}

export function InstantRefundOpt({
  onClick,
  isLoading,
  forceDisabled = false,
  forceDisabledMessage,
}: IInstantRefundOptProps) {
  const { t } = useTranslation('card');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { eligible, errorMessage } = useSafeSelector(
    (state: any) => state.instantRefund
  );
  const payment = useSafeSelector((state: any) => state.payment);
  const isDisabled = eligible === undefined;
  const isNotEligible = eligible === false;
  const [checked, setChecked] = useState(false);
  const isInstallmentDisabled = forceDisabled;
  const isToggleDisabled = isDisabled || isLoading || !eligible || isInstallmentDisabled;
  const helperMessage = isInstallmentDisabled
    ? (forceDisabledMessage ?? t('INSTANT_REFUND_NOT_AVAILABLE_FOR_INSTALLMENTS'))
    : errorMessage;

  useEffect(() => {
    if (!eligible) {
      setChecked(false);
    }
  }, [eligible]);

  useEffect(() => {
    if (!isInstallmentDisabled) return;
    setChecked(false);
    onClick(false);
  }, [isInstallmentDisabled]);

  const handleUserClick = () => {
    if (isToggleDisabled) return;
    setChecked(!checked);
    onClick(!checked);
  };

  const handleShowTerms = () => setShowTermsModal(true);

  const handleHideTerms = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setShowTermsModal(false);
  };

  if (payment.type !== 'card') return null;

  return (
    <div
      className={`w-full pt-4 mt-4 flex flex-col border-t border-t-gray-200 ${
        isNotEligible ? 'opacity-50' : ''
      }`}
    >
      <InstantRefundTermsModal
        isOpen={showTermsModal}
        onClose={handleHideTerms}
      />

      <div
        onClick={handleUserClick}
        className={`${isToggleDisabled ? 'opacity-75' : ''}`}
      >
        <ToggleBox
          text={t('OPT_REFUND')}
          size="sm"
          type="transparent box"
          disabled={isToggleDisabled}
          checked={checked}
        />
      </div>

      <div className="ms-12">
        {!isNotEligible && !errorMessage && (
          <div className="flex items-center gap-1">
            <p className="font-inter text-sm leading-[18px] tracking-[.16px] text-gray-500 [&_button]:inline">
              {t('INSTANT_REFUND_DESC')}{' '}
              <Button
                text={t('LEARN_MORE')}
                color="primary"
                variant="link"
                size="sm"
                onClickHandler={handleShowTerms}
              />
            </p>
          </div>
        )}
        {isNotEligible && (
          <p className="text-sm font-medium leading-6 text-gray-600 italic">
            {t('CARD_NOT_ELIGIBLE')}
          </p>
        )}
      </div>

      {helperMessage && (
        <div
          className={`mt-2 flex items-start gap-2 ${
            isInstallmentDisabled
              ? 'text-sm leading-6 text-gray-600'
              : 'text-sm leading-6 text-orange-700'
          }`}
        >
          <img src={InfoIcon} alt="info" className="mt-[3px] h-4 w-4" />
          <p>{helperMessage}</p>
        </div>
      )}
    </div>
  );
}

export default InstantRefundOpt;
